import '../constants/const.css';
import './css/pageOriginSetting.css';
import Button from 'react-bootstrap/Button';
import React, { useState, useMemo, useEffect } from 'react';
import {
  OriginSettingFormValuesObject,
  OriginSettingFormIndexMap as JsonMap,
  OriginSettingPostAPIParams,
  FurnacesCountDynamicArray,
  BranchOptional,
} from '../entities/originSetting';
import { MESSAGES, PATH_ORIGINSETTING } from '../constants/const';
import { showConfirmMessage, showErrorMessage, showMessage, ShownError, showWarningMessage } from '../components/appPane';
import { JsonResponse, REQ_ORIGIN_SETTING, useApi } from '../logic/classApi';

import { ceilNumber, toISODateStr, toNumberProps } from '../logic/util';
import { LoginService } from '../logic/loginService';
import NumberInput from '../components/numberInput';
import FormInput from '../components/formInput';

const Page = () => {
  const [, setInitialized] = useState<boolean>(false);
  const { branchId, furnacesCount, role } = LoginService.getLoginInfo();

  const isEditablePermission = role === 0;

  const { getRequest, postRequestJson } = useApi();

  const formValueState = useState(new OriginSettingFormValuesObject(furnacesCount));
  const [formValues, setFormValues] = formValueState;

  const [branchOptional, setBranchOptional] = useState<BranchOptional[]>(formValues.branchOptional);

  const columnKeys = [
    'yearPlanSystemFlag',
    'yearPlanExcelFlag',
    'yearResultSystemFlag',
    'yearResultExcelFlag',
    'monthPlanSystemFlag',
    'monthPlanExcelFlag',
    'monthResultSystemFlag',
    'monthResultExcelFlag',
  ];

  useEffect(() => {
    LoginService.setScreenInfo(PATH_ORIGINSETTING);
  }, []);

  useEffect(() => {
    setBranchOptional(formValues.branchOptional);
  }, [formValues.branchOptional]);

  useEffect(() => {
    (async () => {
      try {
        const reqUrl = `${REQ_ORIGIN_SETTING}?branchId=${branchId}`;

        const { payload: data } = await getRequest<JsonResponse>(reqUrl);

        // 支所の炉数と保存済み設定の炉数が異なる場合は現在の支所の炉数でデータを補正
        if (Array.isArray(data.burnLoad) && data.burnLoad.length !== furnacesCount) {
          OriginSettingFormValuesObject.setFurnacesCount(data as OriginSettingFormValuesObject, furnacesCount);
        }

        // ファイルによる設定がすでにある場合は手入力の電力設定が空になっているのでゼロ埋め
        if (!data.generatePowers.length || !data.generatePowers.filter((d: any) => !!d).length) {
          data.generatePowers = new FurnacesCountDynamicArray(furnacesCount + 1).fill(0).map(() => new Array<number>(12).fill(0));
          data.costPowers = new FurnacesCountDynamicArray(furnacesCount + 1).fill(0).map(() => new Array<number>(12).fill(0));
        }

        const newFormValues: OriginSettingFormValuesObject = {
          ...formValues,
          ...data,
        };

        newFormValues.settingMode = newFormValues.settingCsvPath ? 'file' : 'calculation';

        setFormValues(newFormValues);
      } catch (e) {
        console.error(e);
        setInitialized(false);
      }
    })();
    setInitialized(true);
  }, [branchId]);

  const inputByFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget.previousElementSibling as HTMLInputElement)?.click();
  };

  const clearFileInputValue = () => {
    (document.querySelector<HTMLInputElement>('#importFile') as HTMLInputElement).value = '';
  };

  const deleteInputByFile = () => {
    setFormValues({
      ...formValues,
      settingCsvPath: '',
      powerSettingCsvRecords: [],
    });
    clearFileInputValue();
  };

  const handleCheckboxChange = (rowId: number, key: string) => {
    setBranchOptional((b) =>
      b.map((i, id) =>
        id === rowId
          ? {
              ...i,
              [key]: !(i[key] === true),
            }
          : i,
      ),
    );
  };

  const isFileMode = useMemo(() => formValues.settingMode === 'file', [formValues.settingMode]);
  const isCalculationMode = useMemo(() => formValues.settingMode === 'calculation', [formValues.settingMode]);

  const applyByFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget?.files && e.currentTarget?.files.length ? e.currentTarget.files[0] : undefined;

    if (!file) {
      deleteInputByFile();
      return;
    }

    (async () => {
      if (!/.*発電量・売電量.*.csv$/.test(file.name)) {
        const isConfirmed = await showWarningMessage('指定されたファイルは電力設定のCSVファイルではない可能性があります。続行しますか。');
        if (!isConfirmed) {
          clearFileInputValue();
          return;
        }
      }

      const text = await file.text();
      const records = text
        .trim()
        .split(/\r?\n/)
        .map((record) =>
          record
            .replace(/,+$/, '')
            .split(',')
            .map((value) => value.trim()),
        );

      // ヘッダがあれば読み飛ばし (最後のカラムの値が数値ではない)
      if (records.length && !/^[0-9.]+$/.test(records[0].concat().pop() || '')) {
        records.shift();
      }

      if (!records.length) {
        throw new ShownError('読み込んだCSVファイルにレコードがありません');
      }

      // 炉数算出
      const csvFurnacesCount = (records[0].length - 1) / 2;

      if (!csvFurnacesCount || csvFurnacesCount !== parseInt(`${csvFurnacesCount}`)) {
        // CSVフォーマット不正
        throw new ShownError('読み込んだCSVファイルから炉数を取得できません。');
      }

      if (csvFurnacesCount !== furnacesCount) {
        // CSVフォーマット不正
        throw new ShownError('読み込んだCSVファイルのカラムと炉数が一致していません。');
      }

      const powerSettingCsvRecords = records.map((record) => {
        const isoDateStr = toISODateStr(record[0]);
        if (!isoDateStr) {
          throw new ShownError('読み込んだCSVファイルの日付データのISO形式への変換に失敗しました。');
        }
        record[0] = isoDateStr;

        return record;
      });

      setFormValues({
        ...formValues,
        settingCsvPath: file.name,
        furnacesCount,
        powerSettingCsvRecords,
      });
    })().catch((err) => {
      console.error(err);
      clearFileInputValue();

      showErrorMessage((err instanceof ShownError ? err.message : '') || MESSAGES.ERROR_IMPORT_FILE);
    });
  };

  const validateForm = () => {
    const messages = [];

    if (!formValues.pitMax) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('適正ピット残最大値'));
    }

    if (!formValues.pitMin) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('適正ピット残最小値'));
    }

    if (!formValues.ashPitCapacity) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('主灰ピット容量'));
    }

    if (!formValues.rateCapacity) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('定格処理量'));
    }

    if (!formValues.trashDensity) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('ごみ比重'));
    }

    if (isInputEmptyAny) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('焼却負荷率'));
    }

    if (!formValues.ashRate) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('主灰原単位'));
    }

    if (!formValues.truckMainAshCarryOut) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('トラック1台当たり主灰搬出量'));
    }

    if (!formValues.flyAshRate) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('飛灰原単位'));
    }

    if (!formValues.truckFlyAshCarryOut) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('トラック1台当たり飛灰搬出量'));
    }

    if (hasErrorTotalMonthly) {
      messages.push('月別搬入割合は合計が100%になるように入力してください。');
    }

    if (hasErrorTotalWeekly) {
      messages.push('曜日別搬入割合は合計が100%になるように入力してください。');
    }

    if (isFileMode && !formValues.powerSettingCsvRecords.length) {
      messages.push('電力設定ファイルが指定されていません。');
    }

    if (isCalculationMode) {
      if (!isInputAnyGeneratePowers) {
        messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('発電電力量'));
      }

      if (!isInputAnyCostPowers) {
        messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('ごみ売却消費電力量'));
      }
    }

    if (formValues.maxContinuousOperation.some((count) => !count)) {
      messages.push(MESSAGES.ERROR_VALIDATION_INVALID_VALUE('連続運転最大日数'));
    }

    if (messages.length) {
      showErrorMessage(messages);
      return false;
    }

    return true;
  };

  const handleRegister = async () => {
    const isValidForm = validateForm();
    if (!isValidForm) {
      return;
    }

    const confirmResult = await showConfirmMessage(MESSAGES.CONFIRM_POST_FORM);

    if (!confirmResult) {
      return;
    }

    const powerSettingCsvRecords = isFileMode
      ? formValues.powerSettingCsvRecords
      : // 電力設定入力値から発電量・売電量の算出
        (() => {
          const records = [];
          const currentYear = new Date().getFullYear();
          for (let i = 0; i < 12; i++) {
            // 4月始まりでループ
            const month = ((i + 3) % 12) + 1;
            const monthIndex = month - 1;
            const targetYear = monthIndex >= 3 ? currentYear : currentYear + 1;
            for (let targetDate = new Date(targetYear, monthIndex, 1, 9); targetDate.getMonth() === monthIndex; targetDate.setDate(targetDate.getDate() + 1)) {
              const furnacesData = new Array(formValues.furnacesCount).fill(0).map((_v, furnacesIndex) => {
                const generatePower = formValues.generatePowers[formValues.furnacesCount - furnacesIndex][i];
                const costPower = formValues.costPowers[formValues.furnacesCount - furnacesIndex][i];
                const isSunday = targetDate.getDay() === 0;
                const isSaturday = targetDate.getDay() === 6;
                const otherCostPower =
                  isSunday || isSaturday ? formValues.recycleCostPower[1] + formValues.otherCostPower[1] : formValues.recycleCostPower[0] + formValues.otherCostPower[0];
                // 炉別・日毎の売電量と発電量
                const totalCostPower = Number(costPower) + Number(otherCostPower);
                return [Math.max(generatePower - totalCostPower, 0), generatePower];
              });

              // flat処理(データ並び変更含む)
              const flatFurnacesData = furnacesData.map(([salePower]) => `${salePower}`).concat(furnacesData.map(([_, generatePower]) => `${generatePower}`));
              records.push([toISODateStr(targetDate), ...flatFurnacesData]);
            }
          }

          return records;
        })();

    const dummyParams = new OriginSettingPostAPIParams(furnacesCount);
    const params = Object.keys(dummyParams).reduce((params: any, propKey: any) => {
      params[propKey] = (formValues as any)[propKey];
      return params;
    }, dummyParams) as OriginSettingPostAPIParams;

    params.branchId = branchId;
    params.powerSettingCsvRecords = powerSettingCsvRecords;

    // 電力設定がファイルから入力されていた場合は手入力の値を無視
    if (isFileMode) {
      params.generatePowers = [];
      params.costPowers = [];
      params.recycleCostPower = [0, 0];
      params.otherCostPower = [0, 0];
    }

    // 電力設定がファイルから入力されていた場合は手入力の値を無視
    if (isCalculationMode) {
      params.settingCsvPath = '';
    }

    params.branchOptional = branchOptional;

    // 数値の値はnumber型でサーバに送信する
    toNumberProps(params);

    console.log('POST', params);

    showMessage({
      message: MESSAGES.SUCCESS_POST_FORM,
      type: 'success',
      showOnResolved: postRequestJson(REQ_ORIGIN_SETTING, params).then(),
    });
  };

  const totalTrashPitCapacity: number = useMemo(() => {
    return formValues.trashPitCapacity.reduce((total, capacity) => Number(total) + Number(capacity), 0); // 少数のときに文字列連結になってしまうのでNumberとしてキャスト
  }, [formValues.trashPitCapacity]);

  // 焼却負荷率から処理量の逆算
  const operationalProcess: number[] = useMemo(
    () => formValues.burnLoad.map((eachBurnLoad) => (eachBurnLoad / 100) * formValues.rateCapacity),
    [formValues.burnLoad, formValues.rateCapacity],
  );

  const totalMonthly: number = useMemo(
    () => formValues.monthlyCarryCalculation.reduce((total, rate) => total + Number((rate * 100).toFixed()), 0) / 100,
    [formValues.monthlyCarryCalculation],
  );
  const totalWeekly: number = useMemo(
    () => formValues.weeklyCarryCalculation.reduce((total, rate) => total + Number((rate * 100).toFixed()), 0) / 100,
    [formValues.weeklyCarryCalculation],
  );

  const hasErrorTotalMonthly: boolean = useMemo(() => totalMonthly !== 100, [totalMonthly]);
  const hasErrorTotalWeekly: boolean = useMemo(() => totalWeekly !== 100, [totalWeekly]);

  const isInputAnyMonthly: boolean = useMemo(() => totalMonthly > 0, [totalMonthly]);
  const isInputAnyWeekly: boolean = useMemo(() => totalWeekly > 0, [totalWeekly]);

  const isInputAnyGeneratePowers: boolean = useMemo(() => formValues.generatePowers.some((values) => values.some((value) => value)), [formValues.generatePowers]);
  const isInputAnyCostPowers: boolean = useMemo(() => formValues.costPowers.some((values) => values.some((value) => value)), [formValues.costPowers]);

  const isInputEmptyAny: boolean = useMemo(() => formValues.burnLoad.some((burnLoad) => !burnLoad), [formValues.burnLoad]);

  return (
    <div className="screen">
      <br></br>

      <div className="header-itemtitle"></div>
      <div className="item-title">ピット</div>
      <div className="indent-pit d-flex">
        <div className="row-pit-half" style={{ width: '30%' }}>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit">炉数</div>
            </div>
            <div className="d-flex">
              <div className="unit">{formValues.furnacesCount}</div>
              <div className="unit">炉</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit">No.1ごみピット容量</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                min={0}
                data-key={[JsonMap.trashPitCapacity, 0]}
                value={formValues.trashPitCapacity[0]}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit">No.2ごみピット容量</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                min={0}
                data-key={[JsonMap.trashPitCapacity, 1]}
                value={formValues.trashPitCapacity[1]}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit">ごみピット総容量</div>
            </div>
            <div className="d-flex">
              <div className="unit" style={{ maxWidth: '5.5em', overflow: 'auto' }}>
                {totalTrashPitCapacity.toLocaleString()}
              </div>
              <div className="unit">m3</div>
            </div>
          </div>
        </div>
        <div className="row-pit-half">
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit">トラブル時ピット限界値</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="2"
                min={0}
                data-key={[JsonMap.pitLimit]}
                value={formValues.pitLimit}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit required">適正ピット残最大値</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="2"
                min={0}
                data-key={[JsonMap.pitMax]}
                value={formValues.pitMax}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit required">適正ピット残最小値</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="2"
                min={0}
                data-key={[JsonMap.pitMin]}
                value={formValues.pitMin}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit required">主灰ピット容量</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                min={0}
                data-key={[JsonMap.ashPitCapacity]}
                value={formValues.ashPitCapacity}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
          <div className="row-pit">
            <div className="field-name-pit">
              <div className="fieldname-back-pit">適正主灰ピット</div>
            </div>
            <div className="d-flex">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                min={0}
                data-key={[JsonMap.ashPit]}
                value={formValues.ashPit}
                disabled={!isEditablePermission}
              />
              <div className="unit">m3</div>
            </div>
          </div>
        </div>
      </div>
      <br></br>

      <div className="header-itemtitle"></div>
      <div className="item-title">ごみ処理関係</div>
      <div className="indent">
        <div className="row">
          <div className="field-name required">
            <div className="fieldname-back"></div>
            定格処理量
          </div>
          <div className="field-values">
            <div className="field-value">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="2"
                min={0}
                data-number-type="float"
                data-key={[JsonMap.rateCapacity]}
                value={formValues.rateCapacity}
                disabled={!isEditablePermission}
              />
              <div className="unit">t/日</div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="field-name required">
            <div className="fieldname-back"></div>
            ごみ比重
          </div>
          <div className="field-values">
            <div className="field-value">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="2"
                min={0}
                data-key={[JsonMap.trashDensity]}
                value={formValues.trashDensity}
                disabled={!isEditablePermission}
              />
              <div className="unit">t/m3</div>
            </div>
          </div>
        </div>

        {formValues.burnLoad.map((_v, i) => (
          <div className="row">
            <div className="field-name required">
              <div className="fieldname-back"></div>
              {i + 1}炉運転焼却負荷率
            </div>
            <div className="field-values">
              <div className="field-value">
                <NumberInput
                  formValueState={formValueState}
                  className="input-origin"
                  min={0}
                  max={100}
                  data-number-type="integer"
                  data-key={[JsonMap.burnLoad, i]}
                  value={formValues.burnLoad[i]}
                  disabled={!isEditablePermission}
                />
                <div className="unit">%</div>
              </div>
              <div className={`d-flex ml-1 field-value ${!isFinite(ceilNumber(operationalProcess[i], 2)) ? 'd-none' : ''}`}>
                <div className="unit">運転時処理量：</div>
                <div className="unit">{(Number(ceilNumber(operationalProcess[i], 2)) * (i + 1)).toLocaleString()}</div>
                <div className="unit">t/日</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <br></br>

      <div className="header-itemtitle"></div>
      <div className="item-title">主灰・飛灰</div>
      <div className="indent">
        <div className="row">
          <div className="field-name required">
            <div className="fieldname-back"></div>
            主灰原単位
          </div>
          <div className="field-values">
            <div className="field-value">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="5"
                min={0}
                max={1}
                step={0.00001}
                data-key={[JsonMap.ashRate]}
                value={formValues.ashRate}
                disabled={!isEditablePermission}
              />
            </div>
            <div className="row row-layout">
              <div className="field-name required" style={{ marginLeft: '65px' }}>
                <div className="fieldname-back"></div>
                飛灰原単位
              </div>
              <div className="field-values">
                <div className="field-value">
                  <NumberInput
                    formValueState={formValueState}
                    className="input-origin"
                    data-significant-digits="5"
                    min={0}
                    max={1}
                    step={0.00001}
                    data-key={[JsonMap.flyAshRate]}
                    value={formValues.flyAshRate}
                    disabled={!isEditablePermission}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="field-name required">
            <div className="fieldname-back" style={{ height: '50px' }}></div>
            トラック1台当たり主灰搬出量
          </div>
          <div className="field-values">
            <div className="field-value">
              <NumberInput
                formValueState={formValueState}
                className="input-origin"
                data-significant-digits="2"
                min={0}
                max={29}
                data-key={[JsonMap.truckMainAshCarryOut]}
                value={formValues.truckMainAshCarryOut}
                disabled={!isEditablePermission}
              />
              <div className="unit">t/台</div>
            </div>
            <div className="row row-layout">
              <div className="field-name required input-selector">
                <div className="fieldname-back" style={{ height: '50px' }}></div>
                トラック1台当たり飛灰搬出量
              </div>
              <div className="field-values">
                <div className="field-value">
                  <NumberInput
                    formValueState={formValueState}
                    className="input-origin"
                    data-significant-digits="2"
                    min={0}
                    max={29}
                    data-key={[JsonMap.truckFlyAshCarryOut]}
                    value={formValues.truckFlyAshCarryOut}
                    disabled={!isEditablePermission}
                  />
                  <div className="unit">t/台</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <br></br>

      <div className="header-itemtitle"></div>
      <div className="item-title">搬入量計算係数</div>
      <div className="indent">
        <div className="row">
          <div className="field-name">
            <div className="fieldname-back"></div>
            月別搬入割合[%]
          </div>
          <div className="d-flex my-1">
            <div>
              <div className="flex-row">
                <div className="table-row">
                  <div className="cell">4月</div>
                  <div className="cell">5月</div>
                  <div className="cell">6月</div>
                  <div className="cell">7月</div>
                  <div className="cell">8月</div>
                  <div className="cell">9月</div>
                  <div className="cell">10月</div>
                  <div className="cell">11月</div>
                  <div className="cell">12月</div>
                  <div className="cell">1月</div>
                  <div className="cell">2月</div>
                  <div className="cell">3月</div>
                </div>
              </div>
              <div className="flex-row">
                <div className="table-row">
                  {new Array(12).fill(0).map((v, i) => (
                    <NumberInput
                      formValueState={formValueState}
                      key={`calcCell_${i}`}
                      className="cell"
                      data-significant-digits="2"
                      data-key={[JsonMap.monthlyCarryCalculation, i]}
                      value={formValues.monthlyCarryCalculation[i]}
                      min={0}
                      max={100}
                      step={0.5}
                      disabled={!isEditablePermission}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex-column mx-1">
              <div className="text-center">合計</div>
              <div className={`cell ma-auto ${hasErrorTotalMonthly && isInputAnyMonthly ? 'error' : ''}`} style={{ minWidth: '3em' }}>
                {totalMonthly}
              </div>
            </div>
          </div>

          <div className="field-name">
            <div className="fieldname-back"></div>
            曜日別搬入割合[%]
          </div>
          <div className="d-flex my-1">
            <div>
              <div className="flex-row">
                <div className="table-row">
                  <div className="cell">月</div>
                  <div className="cell">火</div>
                  <div className="cell">水</div>
                  <div className="cell">木</div>
                  <div className="cell">金</div>
                  <div className="cell">土</div>
                  <div className="cell">日</div>
                </div>
              </div>
              <div className="flex-row">
                <div className="table-row">
                  {new Array(7).fill(0).map((v, i) => (
                    <NumberInput
                      formValueState={formValueState}
                      key={`calcCell_${i}`}
                      className="cell"
                      data-significant-digits="2"
                      data-key={[JsonMap.weeklyCarryCalculation, i]}
                      value={formValues.weeklyCarryCalculation[i]}
                      min={0}
                      max={100}
                      step={0.5}
                      disabled={!isEditablePermission}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex-column mx-1">
              <div className="text-center">合計</div>
              <div className={`cell ma-auto ${hasErrorTotalWeekly && isInputAnyWeekly ? 'error' : ''}`} style={{ minWidth: '3em' }}>
                {totalWeekly}
              </div>
            </div>
          </div>
        </div>
      </div>

      <br></br>
      <div className="header-draftsetting"></div>
      {/* TODO 文言変更あり */}
      <div className="item-title-draftsetting">その他立案条件</div>
      <div className="indent-draftsetting pb-2">
        <div className="row-draftsetting">
          {(formValues.maxContinuousOperation || []).map((_v, i) => (
            <div key={`continuousOperation_${i}`}>
              <div className="d-flex" style={{ marginLeft: i === 1 ? '35px' : '0px' }}>
                <div className="required-draftsetting field-name-draftsetting">
                  <div className="fieldname-back-draftsetting" style={{ height: '50px' }}></div>
                  {i + 1}号炉<br></br>連続運転最大日数
                </div>
                <div className="field-values-draftsetting" style={{ marginLeft: '50px' }}>
                  <div className="field-value-draftsetting">
                    <NumberInput
                      formValueState={formValueState}
                      className="input-draftsetting"
                      data-number-type="integer"
                      min={0}
                      max={365}
                      data-key={[JsonMap.maxContinuousOperation, i]}
                      value={formValues.maxContinuousOperation[i]}
                      disabled={!isEditablePermission}
                    />
                    <div className="unit-draftsetting">日</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <br></br>

      <div className="header-itemtitle"></div>
      <div className="item-title">電力設定</div>
      <div className="indent">
        <div className="row my-1">
          <div className="field-name">
            <div className="fieldname-back" style={{ height: '27px' }}></div>
            <FormInput formValueState={formValueState} data-key={[JsonMap.settingMode]}>
              <input type="radio" name="settingMode" value="file" checked={isFileMode} defaultChecked disabled={!isEditablePermission} />
            </FormInput>
            <label className="item-sub-title">ファイル設定</label>
          </div>
          <div className="field-values">
            <div className="field-value">
              {isFileMode ? (
                <div>
                  <input type="file" id="importFile" style={{ display: 'none' }} onChange={applyByFile} accept=".csv" disabled={!isEditablePermission} />
                  <Button className="mx-2" disabled={!isEditablePermission} variant="secondary" size="sm" onClick={inputByFile}>
                    ファイル取込み
                  </Button>
                  {formValues.settingCsvPath ? (
                    <Button disabled={!isEditablePermission} className="mx-2" variant="secondary" size="sm" onClick={deleteInputByFile}>
                      取り込みデータクリア
                    </Button>
                  ) : (
                    ''
                  )}

                  <span>{formValues.settingCsvPath}</span>
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>

        <div className="row my-1">
          <div className="field-name">
            <div className="fieldname-back" style={{ height: '27px' }}></div>
            <FormInput formValueState={formValueState} data-key={[JsonMap.settingMode]}>
              <input type="radio" name="settingMode" value="calculation" checked={isCalculationMode} disabled={!isEditablePermission} />
            </FormInput>
            <label className="item-sub-title">入力設定</label>
          </div>
          <div className="field-value d-flex">
            {isCalculationMode ? (
              <div className="d-flex flex-wrap">
                <div className="mr-2 mt-2">
                  <div className="item-sub-title">
                    <div className="fieldname-back" style={{ height: '27px' }}></div>
                    発電電力量[KWh/日]
                  </div>
                  <div className="flex-row">
                    <div className="table-row">
                      <div className="cell wide"></div>
                      <div className="cell wide">4月</div>
                      <div className="cell wide">5月</div>
                      <div className="cell wide">6月</div>
                      <div className="cell wide">7月</div>
                      <div className="cell wide">8月</div>
                      <div className="cell wide">9月</div>
                      <div className="cell wide">10月</div>
                      <div className="cell wide">11月</div>
                      <div className="cell wide">12月</div>
                      <div className="cell wide">1月</div>
                      <div className="cell wide">2月</div>
                      <div className="cell wide">3月</div>
                    </div>
                  </div>
                  <div className="flex-column">
                    {new Array(formValues.furnacesCount + 1)
                      .fill(0)
                      .map((_v, i) => (
                        <div>
                          <div className="table-row">
                            <div className="cell wide">{i > 0 ? `${i}炉運転` : '全炉停止'}</div>
                            {new Array(12).fill(0).map((_v, cellIndex) => (
                              <NumberInput
                                disabled={!isEditablePermission || i === 0}
                                formValueState={formValueState}
                                className="cell wide"
                                min={0}
                                data-key={[JsonMap.generatePowers, i, cellIndex]}
                                value={formValues.generatePowers[i][cellIndex]}
                              />
                            ))}
                          </div>
                        </div>
                      ))
                      .reverse()}
                  </div>
                </div>
                <div className="mr-2 mt-2" style={{ width: '100%' }}>
                  <div className="item-sub-title">消費電力量[KWh/日]</div>
                  <div className="flex-row">
                    <div className="table-row">
                      <div className="cell wide"></div>
                      <div className="cell wide">4月</div>
                      <div className="cell wide">5月</div>
                      <div className="cell wide">6月</div>
                      <div className="cell wide">7月</div>
                      <div className="cell wide">8月</div>
                      <div className="cell wide">9月</div>
                      <div className="cell wide">10月</div>
                      <div className="cell wide">11月</div>
                      <div className="cell wide">12月</div>
                      <div className="cell wide">1月</div>
                      <div className="cell wide">2月</div>
                      <div className="cell wide">3月</div>
                    </div>
                  </div>
                  <div className="flex-column">
                    {new Array(formValues.furnacesCount + 1)
                      .fill(0)
                      .map((_v, i) => (
                        <div className="table-row">
                          <div className="cell wide">{i > 0 ? `${i}炉運転` : '全炉停止'}</div>
                          {new Array(12).fill(0).map((_v, cellIndex) => (
                            <NumberInput
                              formValueState={formValueState}
                              className="cell wide"
                              min={0}
                              data-key={[JsonMap.costPowers, i, cellIndex]}
                              value={formValues.costPowers[i][cellIndex]}
                              disabled={!isEditablePermission}
                            />
                          ))}
                        </div>
                      ))
                      .reverse()}
                  </div>
                </div>

                <div className="mr-2 mt-2 weekly-cost-table" style={{ width: '100%' }}>
                  <div className="item-sub-title">消費電力量[KWh/日]曜日別</div>
                  <div className="flex-row">
                    <div className="table-row">
                      <div className="cell wide">曜日</div>
                      <div className="cell wide">リサイクル</div>
                      <div className="cell wide">その他</div>
                    </div>
                  </div>
                  <div className="flex-row">
                    <div className="table-row">
                      <div className="cell wide">月～金</div>
                      <NumberInput
                        formValueState={formValueState}
                        className="cell wide"
                        min={0}
                        data-key={[JsonMap.recycleCostPower, 0]}
                        value={formValues.recycleCostPower[0]}
                        disabled={!isEditablePermission}
                      />
                      <NumberInput
                        formValueState={formValueState}
                        className="cell wide"
                        min={0}
                        data-key={[JsonMap.otherCostPower, 0]}
                        value={formValues.otherCostPower[0]}
                        disabled={!isEditablePermission}
                      />
                    </div>
                  </div>
                  <div className="flex-row">
                    <div className="table-row">
                      <div className="cell wide">土日</div>
                      <NumberInput
                        formValueState={formValueState}
                        className="cell wide"
                        min={0}
                        data-key={[JsonMap.recycleCostPower, 1]}
                        value={formValues.recycleCostPower[1]}
                        disabled={!isEditablePermission}
                      />
                      <NumberInput
                        formValueState={formValueState}
                        className="cell wide"
                        min={0}
                        data-key={[JsonMap.otherCostPower, 1]}
                        value={formValues.otherCostPower[1]}
                        disabled={!isEditablePermission}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </div>

      <br></br>

      <div className="header-itemtitle"></div>
      <div className="item-title">項目設定</div>
      <div className="indent">
        <div className="row">
          <div className="mr-2 mt-2">
            <table className="table-auto text-center gray-bordered-table" style={{ borderCollapse: 'separate', borderSpacing: 0, height: '100%' }}>
              <thead>
                <tr>
                  <th colSpan={1} className="item-style" />
                  <th colSpan={4} className="item-style">
                    年間
                  </th>
                  <th colSpan={4} className="item-style">
                    月間
                  </th>
                </tr>
                <tr>
                  <th rowSpan={2} className="item-style">
                    項目名
                  </th>
                  <th colSpan={2} className="item-style">
                    計画
                  </th>
                  <th colSpan={2} className="item-style">
                    実績
                  </th>
                  <th colSpan={2} className="item-style">
                    計画
                  </th>
                  <th colSpan={2} className="item-style">
                    実績
                  </th>
                </tr>
                <tr>
                  {new Array(4).fill(0).map((_v, i) => (
                    <React.Fragment key={i}>
                      <th className="item-style" style={{ textAlign: 'left' }}>
                        システム表示
                      </th>
                      <th className="item-style" style={{ textAlign: 'left' }}>
                        Excel出力
                        <br />
                        ファイル表示
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {branchOptional.map((i, rowId) => (
                  <tr key={i.optionalId} style={{ textAlign: 'left' }}>
                    <td className="item-style">{i.optionalShowName}</td>
                    {columnKeys.map((key, colId) => {
                      const val = i[key];
                      return (
                        <td key={colId} className="item-style bg-white checkbox-td">
                          {val === true || val === false ? (
                            <label className="center-checkbox">
                              <input type="checkbox" checked={val === true} onChange={() => handleCheckboxChange(rowId, key)} disabled={!isEditablePermission} />
                            </label>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="button-origin">
        <Button disabled={!isEditablePermission} variant="success" onClick={handleRegister}>
          登録
        </Button>
      </div>

      <br />
    </div>
  );
};

export default Page;
